import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai";
import { useLocation, useNavigate, type NavigateFunction } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useInactivityTimeout } from "~/utils/receptionUtils";
import { languageAtom, volumeAtom } from "~/Atom";
import ReceptionHeader from "~/components/ReceptionHeader";
import { getAppSettingsData, getPhoneBooksEntity } from "~/utils/receptionDB";
import type { AppSettingsNormalized } from "~/utils/receptionDB";
import {
  NotificationType,
  CallType,
  ScreenType,
  type PhoneBooksEntity,
  type MenuWordEntity,
} from "~/utils/receptionDB.types";
import { mapErrorCode, buildErrorPath } from "~/utils/error";
import { PlaySound } from "~/SoundUtils";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import { runReceptionBack } from "~/utils/receptionNavigation";
import "~/css/reception/VisitorConfirmationPage.css";
import { ROUTE } from "~/constants/routes";

const NO_CALL_DESTINATION = "2009";

export type VisitorConfirmationNavigationState = {
  targetGroupId?: number;
  targetGroupUserId?: number;
  companyName?: string;
  visitorName?: string;
  department?: string;
  personInCharge?: string;
  // VisitorSelection から自動遷移された場合は true を付与する
  fromSelectionAutoRedirect?: boolean;
};

// POST /v1/receptions/general リクエストボディ生成ヘルパー
function buildReceptionBody(
  targetGroupUserIdParam: number | undefined,
  targetGroupIdParam: number | undefined,
  visitorName: string | null,
  affiliation: string | null,
  groupName: string,
  userName: string,
  notificationType: string,
  buttonName: string
): Record<string, unknown> {
  return {
    route: targetGroupUserIdParam ? "USER" : "GROUP",
    affiliation: affiliation,
    visitorName: visitorName,
    business: undefined,
    groupId: !targetGroupUserIdParam && targetGroupIdParam ? targetGroupIdParam : undefined,
    groupName,
    groupUserId: targetGroupUserIdParam,
    userName,
    buttonName,
    notificationType,
  };
}

// ボタン名解決ヘルパー（純粋関数）
function resolveButtonName(
  menuWords: MenuWordEntity[],
  groupList: PhoneBooksEntity["groupList"],
  targetGroupUserIdParam: number | undefined,
  targetGroupIdParam: number | undefined,
  languageCode: string
): string {
  const hasUser = !!targetGroupUserIdParam;
  let index = 0;
  if (!hasUser && targetGroupIdParam) {
    const group = groupList.find((g) => g.groupId === targetGroupIdParam);
    index = group?.displayId ?? 0;
  }
  const entity = menuWords[index];
  if (!entity) return "";
  const item = entity.items.find((it) => it.languageCode === languageCode);
  if (!item) return "";
  return hasUser ? item.menuWords.userButtonTitle : item.menuWords.groupButtonTitle;
}

// エンティティ情報解決ヘルパー（純粋関数: テスト容易化 + handleOk の複雑度削減）
function resolveEntityInfo(
  entity: PhoneBooksEntity,
  targetGroupUserId: number | undefined,
  targetGroupId: number | undefined
): { notificationType: string; callType: string | null; extensionNumber: string | null } {
  if (targetGroupUserId) {
    const user = entity.userList.find((u) => u.groupUserId === targetGroupUserId);
    if (user) {
      return {
        notificationType: user.notificationType,
        callType: user.callType,
        extensionNumber: user.extensionNumber,
      };
    }
  } else if (targetGroupId) {
    const group = entity.groupList.find((g) => g.groupId === targetGroupId);
    if (group) {
      return {
        notificationType: group.notificationType,
        callType: group.callType,
        extensionNumber: group.extensionNumber,
      };
    }
  }
  return { notificationType: "", callType: null, extensionNumber: null };
}

// 電話フロー分岐ヘルパー（trueを返した場合はフロー処理済み）
function processCallPlanFlow(
  plan: string,
  notificationType: string,
  callType: string | null,
  targetGroupUserId: number | undefined,
  extensionNumber: string | null,
  navigate: NavigateFunction
): boolean {
  const isCallPlan = plan === "CALL" || plan === "CHAT_CALL";
  if (!isCallPlan || notificationType !== NotificationType.Call) return false;

  if (callType === CallType.Phone && extensionNumber) {
    navigate(ROUTE.RECEPTION.CALLING, { state: { extensionNumber } });
    return true;
  }

  const screen = targetGroupUserId ? ScreenType.EMPLOYEE_LIST : ScreenType.DEPARTMENT_LIST;
  navigate(buildErrorPath(mapErrorCode(screen, Number(NO_CALL_DESTINATION))));
  return true;
}

/**
 * 来訪先確認ページ。
 * URLパラメータから来訪先グループ・ユーザー名を受け取り、確認ダイアログを表示する。
 *
 * 主な責務:
 * - targetGroupName・targetUserName を確認テキストとして表示する
 * - OK押下時はプラン・通知タイプに応じて電話画面または受付完了処理へ遷移する
 * - キャンセル/戻る操作: 通常は `visitor-selection` へ検索パラメータを維持して戻る
 * - ただし `VisitorSelectionPage` から自動遷移（検索結果が1件）で遷移してきた場合は
 *   元の入力画面である `ReceptionConversationDepartmentPic` へ直接戻る
 */
// eslint-disable-next-line complexity
const VisitorConfirmationPage: React.FC = () => {
  const [volume] = useAtom(volumeAtom);
  const [language] = useAtom(languageAtom);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dbData, setDbData] = useState<AppSettingsNormalized | undefined>(undefined);
  const [phoneBooksEntity, setPhoneBooksEntity] = useState<PhoneBooksEntity | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
  });

  // navigate.state から検索条件と来訪先IDを取得する
  const pageState = (location.state as VisitorConfirmationNavigationState | null) ?? null;
  const visitorName = pageState?.visitorName ?? null;
  const companyName = pageState?.companyName ?? null;
  const departmentParam = pageState?.department ?? null;
  const personInChargeParam = pageState?.personInCharge ?? null;
  const targetGroupIdParam = pageState?.targetGroupId;
  const targetGroupUserIdParam = pageState?.targetGroupUserId;
  const fromSelectionAutoRedirect = pageState?.fromSelectionAutoRedirect ?? false;

  // IndexedDB からアプリ設定を取得する
  useEffect(() => {
    void getAppSettingsData()
      .then((data) => {
        if (data) setDbData(data);
      })
      .catch((error) => {
        console.error("アプリ設定の読み込みに失敗しました:", error);
      });
  }, []);

  // IndexedDB から電話帳データを取得する
  useEffect(() => {
    void getPhoneBooksEntity()
      .then((entity) => {
        if (entity) setPhoneBooksEntity(entity);
      })
      .catch((error) => {
        console.error("来訪先データの読み込みに失敗しました:", error);
      });
  }, []);

  // 現在の言語コードに対応するローカライズ名を返す
  const getLocalizedName = useCallback(
    (names: { languageCode: string; name: string }[]): string => {
      const langCode = language.substring(0, 2);
      const nameItem = names.find((n) => n.languageCode === langCode);
      return nameItem?.name ?? names[0]?.name ?? "";
    },
    [language]
  );

  // targetGroupIdからグループ名と階層名を解決する
  const { targetGroupName, targetGroupNameHierarchy } = useMemo(() => {
    if (!targetGroupIdParam || !phoneBooksEntity) {
      return {
        targetGroupName: "",
        targetGroupNameHierarchy: "",
      };
    }

    const groupById = new Map(phoneBooksEntity.groupList.map((g) => [g.groupId, g]));
    const groupChain: PhoneBooksEntity["groupList"][number][] = [];
    const visited = new Set<number>();
    let currentGroupId: number | null = targetGroupIdParam;

    while (currentGroupId !== null) {
      if (visited.has(currentGroupId)) break;
      visited.add(currentGroupId);

      const group = groupById.get(currentGroupId);
      if (!group) break;

      groupChain.push(group);
      currentGroupId = group.parentGroupId;
    }

    const localizedChain = groupChain
      .reverse()
      .map((group) => getLocalizedName(group.names))
      .filter(Boolean);

    const parentGroupName = localizedChain[0] ?? "";
    const childGroupName = localizedChain[localizedChain.length - 1] ?? "";
    const hierarchy =
      groupChain.length > 1 ? `${parentGroupName}/${childGroupName}` : childGroupName;

    const leaf = childGroupName;

    return {
      targetGroupName: leaf,
      targetGroupNameHierarchy: hierarchy,
    };
  }, [targetGroupIdParam, phoneBooksEntity, getLocalizedName]);

  // targetGroupUserIdからユーザー名を解決する
  const targetUserName = useMemo(() => {
    if (!targetGroupUserIdParam || !phoneBooksEntity) return "";
    const user = phoneBooksEntity.userList.find((u) => u.groupUserId === targetGroupUserIdParam);
    return user ? getLocalizedName(user.names) : "";
  }, [targetGroupUserIdParam, phoneBooksEntity, getLocalizedName]);

  // TTS読み上げ用のユーザー名: 日本語の場合はkanaNameを使用、それ以外は表示名と同じ
  const targetUserNameForTTS = useMemo(() => {
    if (!targetGroupUserIdParam || !phoneBooksEntity) return "";
    const user = phoneBooksEntity.userList.find((u) => u.groupUserId === targetGroupUserIdParam);
    if (!user) return "";
    const isJapanese = language.substring(0, 2) === "ja";
    if (isJapanese && user.kanaName) {
      return user.kanaName;
    }
    return getLocalizedName(user.names);
  }, [targetGroupUserIdParam, phoneBooksEntity, getLocalizedName, language]);

  const confirmationMessage = useMemo(
    () =>
      [targetGroupName, targetUserName, t("visitorConfirmation.confirmSuffix")]
        .filter(Boolean)
        .join("\n"),
    [targetGroupName, targetUserName, t]
  );

  // 確認テキストをTTSで読み上げる
  useEffect(() => {
    if (!targetGroupName && !targetUserNameForTTS) return;

    // 表示は name、読み上げは kanaName（日本語のみ）を使用
    const confirmText = [
      targetGroupName,
      targetUserNameForTTS,
      t("visitorConfirmation.confirmSuffix"),
    ]
      .filter(Boolean)
      .join(" ");
    void playWithCooldown(confirmText).catch((error) => {
      console.error("visitorConfirmation playVoice failed:", error);
    });
  }, [targetGroupName, targetUserNameForTTS, t, playWithCooldown]);

  useInactivityTimeout(navigate);

  // 戻るボタン: visitor-selectionへ検索パラメータを維持して戻る
  const handleBack = useCallback(() => {
    PlaySound("push", volume);
    // VisitorSelection から自動遷移してきた場合は、元の入力画面である
    // ReceptionConversationDepartmentPic へ戻る（直接遷移）
    if (fromSelectionAutoRedirect) {
      navigate(ROUTE.RECEPTION.CONVERSATION.DEPARTMENT_PIC, {
        state: {
          companyName: companyName ?? undefined,
          visitorName: visitorName ?? undefined,
        },
      });
      return;
    }

    runReceptionBack({
      kind: "router-history",
      navigate,
    });
  }, [
    navigate,
    volume,
    fromSelectionAutoRedirect,
    companyName,
    visitorName,
    departmentParam,
    personInChargeParam,
  ]);

  // OKボタン: プラン・通知タイプに応じて電話画面/受付完了処理へ分岐する
  // eslint-disable-next-line complexity
  const handleOk = useCallback(async () => {
    if (isSubmitting) return;
    PlaySound("push", volume);
    if (!phoneBooksEntity) return;
    setIsSubmitting(true);

    const { notificationType, callType, extensionNumber } = resolveEntityInfo(
      phoneBooksEntity,
      targetGroupUserIdParam,
      targetGroupIdParam
    );

    if (
      processCallPlanFlow(
        phoneBooksEntity.plan,
        notificationType,
        callType,
        targetGroupUserIdParam,
        extensionNumber,
        navigate
      )
    ) {
      setIsSubmitting(false);
      return;
    }

    // その他: POST /v1/receptions/general → 受付完了ページへ遷移
    const buttonName = resolveButtonName(
      dbData?.menuWords ?? [],
      phoneBooksEntity.groupList,
      targetGroupUserIdParam,
      targetGroupIdParam,
      language.substring(0, 2)
    );

    try {
      await apiClient.post(
        buildIfaceApiUrl("v1/receptions/general"),
        buildReceptionBody(
          targetGroupUserIdParam,
          targetGroupIdParam,
          visitorName,
          companyName,
          targetGroupNameHierarchy,
          targetUserName,
          notificationType,
          buttonName
        ),
        {
          headers: createIfaceHeaders(true),
        }
      );
      navigate(ROUTE.RECEPTION.COMPLETE);
    } catch (error) {
      const rawCode = (error as { response?: { data?: { errorCode?: number | string } } })?.response
        ?.data?.errorCode;
      const numericCode =
        typeof rawCode === "number"
          ? rawCode
          : typeof rawCode === "string"
            ? Number(rawCode) || undefined
            : undefined;
      const screen = targetGroupUserIdParam ? ScreenType.EMPLOYEE_LIST : ScreenType.DEPARTMENT_LIST;
      const uiCode = mapErrorCode(screen, numericCode);
      navigate(buildErrorPath(uiCode));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    volume,
    phoneBooksEntity,
    navigate,
    targetGroupIdParam,
    targetGroupUserIdParam,
    visitorName,
    companyName,
    targetGroupNameHierarchy,
    targetUserName,
    language,
    dbData,
  ]);

  return (
    <div className="App-page visitor-confirmation-page">
      <ReceptionHeader showBackButton onBackButtonClick={handleBack} />

      <main className="visitor-confirmation-page__content">
        <section
          className="visitor-confirmation-page__dialog"
          aria-labelledby="visitor-confirmation-message"
        >
          <p
            id="visitor-confirmation-message"
            className="visitor-confirmation-page__message typo-h3"
          >
            {confirmationMessage}
          </p>

          <div className="visitor-confirmation-page__actions">
            <button
              type="button"
              className="visitor-confirmation-page__button visitor-confirmation-page__button--secondary typo-button"
              onClick={handleBack}
            >
              {t("visitorConfirmation.btnCancel")}
            </button>
            <button
              type="button"
              className="visitor-confirmation-page__button visitor-confirmation-page__button--primary typo-button"
              onClick={handleOk}
              disabled={isSubmitting}
            >
              {t("visitorConfirmation.btnOk")}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default VisitorConfirmationPage;
